import { useEffect, useState } from "react";
import {
  AddFlex,
  ButtonWithLoader,
  CustomInput,
  InputLabel,
} from "../Components/reusableStyles";
import {
  Currency,
  callNonStoreApi,
  formatPrice,
  generateUUID,
  isNullOrUndefined,
} from "../utils/utils";
import TextEditor from "../Components/TextEditor";
import "react-quill/dist/quill.snow.css";
import { IOSSwitch } from "../Components/CustomSwitch";
import { UpdateSubscriptionPlans } from "../utils/ApiEndpoints";
import { useDispatch } from "react-redux";
import { setPlansDataAction } from "../Store/appTempSlice";
import { MenuItem, Select } from "@mui/material";

const PRICING_TYPES = {
  QUOTAS: "quotas",
  PAID: "paid",
};

function CreateUpdatePlan({ selectedPlan }) {
  const [initialLoading, setInitialLoading] = useState(true);
  const [apiLoading, setApiLoading] = useState(false);
  const dispatch = useDispatch();
  const [plan, setPlan] = useState({
    id: generateUUID(),
    name: "",
    code: "",
    pricingType: PRICING_TYPES.PAID,
    subText: "",
    version: 0,
    isFree: false,
    quotas: {},
    prices: {
      monthly: {
        INR: 0,
        USD: 0,
      },
      annualy: {
        INR: 0,
        USD: 0,
      },
    },
    isActive: false,
    featureText: "",
    features: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  });
  const handleSetQuotas = (key, value) => {
    setPlan((prev) => {
      if (isNullOrUndefined(plan.quotas)) {
        return {
          ...prev,
          quotas: {
            [key]: value,
          },
        };
      }
      return {
        ...prev,
        quotas: {
          ...prev.quotas,
          [key]: value,
        },
      };
    });
  };
  const handleSetPlan = (key, value) => {
    setPlan((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  };
  const handleSetPrices = (billingType, currency, isStrikeOff, price) => {
    setPlan((prev) => {
      return {
        ...prev,
        prices: {
          ...prev.prices,
          [billingType]: {
            ...prev.prices[billingType],
            [currency]: {
              ...prev.prices[billingType][currency],
              [isStrikeOff ? "strike" : "main"]: price * 100,
            },
          },
        },
      };
    });
  };

  const getHumanReadablePrice = (price, currency) => {
    if (isNullOrUndefined(price) || price === 0) return "";
    return price / 100;
    return formatPrice(parseInt(price), currency);
  };

  const handleAddUpdatePlan = async () => {
    try {
      setApiLoading(true);
      const response = await callNonStoreApi(UpdateSubscriptionPlans, plan);
      dispatch(setPlansDataAction(response.data));
    } catch (error) {
      console.log(error);
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
    if (!isNullOrUndefined(selectedPlan)) {
      setPlan(selectedPlan);
    }
    setInitialLoading(false);
  }, []);
  return (
    !initialLoading && (
      <AddFlex flexDirection="column" gap="25px" padding="0 0 30px 0">
        <CustomInput
          label={"Plan Name"}
          value={plan.name}
          placeholder={"Eg: Professional"}
          onChange={(text) => {
            handleSetPlan("name", text);
          }}
        />
        <div></div>
        <CustomInput
          label={"Plan Sub Text"}
          value={plan.subText}
          placeholder={"Eg: Describe the plan in one line"}
          onChange={(text) => {
            handleSetPlan("subText", text);
          }}
        />
        <div></div>
        <CustomInput
          label={"Plan Code"}
          value={plan.code}
          placeholder={"Eg: STARTER_PLAN"}
          onChange={(text) => {
            handleSetPlan("code", text);
          }}
        />
        <div></div>
        <TextEditor
          value={plan.featureText}
          label={"Enter plan description"}
          getTextEditorInput={(content) => {
            handleSetPlan("featureText", content);
          }}
          placeholder={"Enter the plan details here"}
        />
        <AddFlex flexDirection="column">
          <InputLabel>Pricing Type</InputLabel>
          <Select
            value={plan.pricingType}
            onChange={(e) => {
              handleSetPlan("pricingType", e.target.value);
            }}
            displayEmpty={false}
            size="small"
            sx={{ margin: "5px 5px 0 0" }}
            inputProps={{ "aria-label": "Without label" }}
          >
            <MenuItem value={PRICING_TYPES.PAID}>Paid</MenuItem>
            <MenuItem value={PRICING_TYPES.QUOTAS}>Quotas</MenuItem>
          </Select>
        </AddFlex>
        {plan.pricingType === PRICING_TYPES.PAID ? (
          <>
            <AddFlex flexDirection="column">
              <InputLabel>Monthly Pricing</InputLabel>
              <AddFlex alignItems="center" gap="12px">
                <CustomInput
                  value={getHumanReadablePrice(
                    plan.prices?.monthly[Currency.INR]?.main,
                    Currency.INR
                  )}
                  leftWidget={"INR"}
                  placeholder={"Eg: 500"}
                  type={"number"}
                  onChange={(text) => {
                    handleSetPrices("monthly", Currency.INR, false, text);
                  }}
                />
                <CustomInput
                  leftWidget={"USD"}
                  type={"number"}
                  value={getHumanReadablePrice(
                    plan.prices?.monthly[Currency.USD]?.main,
                    Currency.USD
                  )}
                  placeholder={"Eg: 20"}
                  onChange={(text) => {
                    handleSetPrices("monthly", Currency.USD, false, text);
                  }}
                />
              </AddFlex>
            </AddFlex>
            <AddFlex flexDirection="column">
              <InputLabel>Monthly Strike Off Pricing</InputLabel>
              <AddFlex alignItems="center" gap="12px">
                <CustomInput
                  value={getHumanReadablePrice(
                    plan.prices?.monthly[Currency.INR]?.strike,
                    Currency.INR
                  )}
                  leftWidget={"INR"}
                  placeholder={"Eg: 500"}
                  onChange={(text) => {
                    handleSetPrices("monthly", Currency.INR, true, text);
                  }}
                />
                <CustomInput
                  leftWidget={"USD"}
                  value={getHumanReadablePrice(
                    plan.prices?.monthly[Currency.USD]?.strike,
                    Currency.USD
                  )}
                  placeholder={"Eg: 20"}
                  onChange={(text) => {
                    handleSetPrices("monthly", Currency.USD, true, text);
                  }}
                />
              </AddFlex>
            </AddFlex>
            <AddFlex flexDirection="column">
              <InputLabel>Annual Pricing</InputLabel>
              <AddFlex alignItems="center" gap="12px">
                <CustomInput
                  value={getHumanReadablePrice(
                    plan.prices?.annualy[Currency.INR]?.main,
                    Currency.INR
                  )}
                  leftWidget={"INR"}
                  type={"number"}
                  placeholder={"Eg: 500"}
                  onChange={(text) => {
                    handleSetPrices("annualy", Currency.INR, false, text);
                  }}
                />
                <CustomInput
                  leftWidget={"USD"}
                  type={"number"}
                  value={getHumanReadablePrice(
                    plan.prices?.annualy[Currency.USD]?.main,
                    Currency.USD
                  )}
                  placeholder={"Eg: 20"}
                  onChange={(text) => {
                    handleSetPrices("annualy", Currency.USD, false, text);
                  }}
                />
              </AddFlex>
            </AddFlex>
            <AddFlex flexDirection="column">
              <InputLabel>Annual Strike Off Pricing</InputLabel>
              <AddFlex alignItems="center" gap="12px">
                <CustomInput
                  value={getHumanReadablePrice(
                    plan.prices?.annualy[Currency.INR]?.strike,
                    Currency.INR
                  )}
                  leftWidget={"INR"}
                  type={"number"}
                  placeholder={"Eg: 500"}
                  onChange={(text) => {
                    handleSetPrices("annualy", Currency.INR, true, text);
                  }}
                />
                <CustomInput
                  leftWidget={"USD"}
                  type={"number"}
                  placeholder={"Eg: 20"}
                  onChange={(text) => {
                    handleSetPrices("annualy", Currency.USD, true, text);
                  }}
                />
              </AddFlex>
            </AddFlex>
          </>
        ) : (
          <>
            <AddFlex
              style={{ flexShrink: 0 }}
              margin="0 0 30px 0"
              gap="20px"
              flexDirection="column"
            >
              <CustomInput
                value={plan.quotas?.bookings}
                label={"Bookings Quota"}
                placeholder={"Eg: 50"}
                type={"number"}
                onChange={(text) => {
                  handleSetQuotas("bookings", text);
                }}
              />
              <div></div>
              <CustomInput
                value={plan.quotas?.leads}
                label={"Leads Quota"}
                placeholder={"Eg: 50"}
                type={"number"}
                onChange={(text) => {
                  handleSetQuotas("leads", text);
                }}
              />
              <div></div>

              <CustomInput
                value={plan.quotas?.testimonials}
                label={"Testimonials Quota"}
                placeholder={"Eg: 50"}
                type={"number"}
                onChange={(text) => {
                  handleSetQuotas("testimonials", text);
                }}
              />
            </AddFlex>
          </>
        )}
        <AddFlex gap="12px">
          <InputLabel>Activate Plan</InputLabel>
          <IOSSwitch
            checked={plan.isActive}
            onChange={(e) => {
              handleSetPlan("isActive", e.target.checked);
            }}
          />
        </AddFlex>
        <ButtonWithLoader
          title={"Update Plans"}
          onClick={handleAddUpdatePlan}
          loading={apiLoading}
        />
      </AddFlex>
    )
  );
}

export default CreateUpdatePlan;
